import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import moment from "jalali-moment";
import { useMutation } from "@tanstack/react-query";
import { useNotifications } from "@toolpad/core/useNotifications";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddAlarmIcon from "@mui/icons-material/AddAlarm";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { getRoutes } from "src/utilities/routes";
import { useAuth } from "src/context/auth";
import { IAgreement } from "src/interface/IAgreement";
import { ITransaction } from "src/interface/ITransaction";
import { TransactionType } from "src/interface/enum/TransactionType";
import { BootstrapTooltip } from "src/utilities";

interface UserAgreementsProps {
  userId?: number;
  onClose?: () => void;
  refetchUser?: () => void;
  onAgreementsUpdate?: (count: number) => void;
}

const UserAgreements: React.FC<UserAgreementsProps> = ({
  userId,
  onClose,
  refetchUser,
  onAgreementsUpdate,
}) => {
  const Routes = getRoutes();
  const notifications = useNotifications();
  const { user } = useAuth();

  const [data, setData] = useState<IAgreement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [quickFilterValue, setQuickFilterValue] = useState<string>("");
  const quickFilterRef = useRef<HTMLInputElement>(null);

  // دریافت قراردادهای کاربر
  const { mutateAsync: getUserAgreements, isPending: isPendingAgreements } =
    useMutation({
      mutationFn: async () => {
        if (!userId) return { items: [], totalCount: 0 };

        const url = `${Routes.AgreementsByCustomerRoutes}${userId.toString()}?page=${paginationModel.page}&pageSize=${paginationModel.pageSize}${quickFilterValue ? `&filter=${encodeURIComponent(quickFilterValue)}` : ""}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token || ""}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
      },
      onSuccess: (responseData: {
        items: IAgreement[];
        totalCount: number;
      }) => {
        if (responseData && responseData.items) {
          setData(responseData.items);
          setRowCount(responseData.totalCount);
          // به‌روزرسانی تعداد قراردادها در تب
          onAgreementsUpdate?.(responseData.totalCount);
        }
      },
      onError: (error) => {
        notifications.show("خطا در دریافت قراردادهای کاربر", {
          severity: "error",
          autoHideDuration: 3000,
        });
        console.error("Error fetching user agreements:", error);
      },
    });

  // صدور سند بدهکاری
  const { mutateAsync: insertTransaction, isPending: isTransactionLoading } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(Routes.TransactionRoutes, {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });
        return await response.json();
      },
      onSuccess: (responseData) => {
        if (responseData.status === true) {
          notifications.show("سند بدهکاری با موفقیت صادر شد", {
            severity: "success",
            autoHideDuration: 3000,
          });
          getUserAgreements();
        }
      },
      onError: (error) => {
        notifications.show("خطا در صدور سند بدهکاری", {
          severity: "error",
          autoHideDuration: 3000,
        });
        console.error("Error creating transaction:", error);
      },
    });

  // حذف قرارداد
  const { mutateAsync: deleteAgreement, isPending: isDeleteLoading } =
    useMutation({
      mutationFn: async (agreeId: string) => {
        const response = await fetch(Routes.AgreementsRoutes + agreeId, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        });
        return await response.json();
      },
      onSuccess: (responseData) => {
        if (responseData.status) {
          notifications.show("قرارداد با موفقیت حذف شد", {
            severity: "success",
            autoHideDuration: 3000,
          });
          getUserAgreements();
        } else {
          notifications.show("خطا در حذف قرارداد", {
            severity: "error",
            autoHideDuration: 3000,
          });
        }
      },
      onError: (error) => {
        notifications.show("خطا در حذف قرارداد", {
          severity: "error",
          autoHideDuration: 3000,
        });
        console.error("Error deleting agreement:", error);
      },
    });

  // هندلر صدور سند بدهکاری
  const handleTransaction = async (params: any) => {
    const newTransaction: ITransaction = {
      agreementId: params.row.id,
      amount: params.row.price,
      description: "بابت قرارداد",
      customerRef: params.row.customerRef,
      isDebit: true,
      transactionDate: params.row.startedAt && new Date(params.row.startedAt),
      transactionType: TransactionType.Contractual,
    };
    await insertTransaction(newTransaction);
  };

  // هندلر حذف قرارداد
  const handleDelete = async (id: number) => {
    if (window.confirm("آیا از حذف این قرارداد اطمینان دارید؟")) {
      await deleteAgreement(id.toString());
    }
  };

  // هندلر تغییر مقدار فیلتر
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuickFilterValue(event.target.value);
    },
    []
  );

  // هندلر کلید Enter
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        event.preventDefault();
        getUserAgreements();
      }
    },
    [getUserAgreements]
  );

  // تعریف ستون‌های جدول
  const columns: GridColDef[] = [
    {
      field: "actions",
      headerName: "عملیات",
      align: "center",
      headerAlign: "center",
      sortable: false,
      minWidth: 200,
      type: "actions",
      getActions: (params) => {
        const hasTransactions =
          params.row.transactions?.filter(
            (t: ITransaction) => t.transactionStatus === 1
          ).length > 0;
        const hasFollowups = params.row.followups?.length > 0;

        return [
          <BootstrapTooltip title="مشاهده جزئیات">
            <GridActionsCellItem
              icon={<VisibilityIcon color="primary" />}
              label="مشاهده"
              onClick={() => {
                // TODO: نمایش جزئیات قرارداد
                notifications.show("این قابلیت در حال توسعه است", {
                  severity: "info",
                  autoHideDuration: 2000,
                });
              }}
            />
          </BootstrapTooltip>,
          <BootstrapTooltip title="ویرایش قرارداد">
            <GridActionsCellItem
              icon={<EditNoteIcon color="primary" />}
              label="ویرایش"
              onClick={() => {
                // TODO: ویرایش قرارداد
                notifications.show("این قابلیت در حال توسعه است", {
                  severity: "info",
                  autoHideDuration: 2000,
                });
              }}
            />
          </BootstrapTooltip>,
          <BootstrapTooltip title="صدور سند بدهکاری">
            <GridActionsCellItem
              icon={
                <LocalAtmIcon
                  color={hasTransactions ? "disabled" : "secondary"}
                />
              }
              label="صدور سند"
              disabled={hasTransactions}
              onClick={() => handleTransaction(params)}
            />
          </BootstrapTooltip>,
          <BootstrapTooltip title="حذف قرارداد">
            <GridActionsCellItem
              icon={
                <DeleteIcon
                  color={hasTransactions || hasFollowups ? "disabled" : "error"}
                />
              }
              label="حذف"
              disabled={hasTransactions || hasFollowups}
              onClick={() => handleDelete(params.id as number)}
            />
          </BootstrapTooltip>,
        ];
      },
    },
    {
      field: "startedAt",
      headerName: "تاریخ شروع",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        params.value
          ? moment
              .from(params.value, "en", "YYYY-MM-DD")
              .format("jYYYY/jMM/jDD")
          : "-",
    },
    {
      field: "price",
      headerName: "مبلغ (ریال)",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<any, number>) =>
        params.value ? params.value.toLocaleString() : "-",
    },
    {
      field: "sessionCounter",
      headerName: "تعداد فصل",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, number>) =>
        params.value || "-",
    },
    {
      field: "delayFactor",
      headerName: "ضریب تاخیر",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, number>) =>
        params.value || "-",
    },
    {
      field: "status",
      headerName: "وضعیت",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, any>) => {
        const hasTransactions =
          params.row.transactions?.filter(
            (t: ITransaction) => t.transactionStatus === 1
          ).length > 0;
        const hasFollowups = params.row.followups?.length > 0;

        if (hasTransactions) {
          return <Chip label="فعال" color="success" size="small" />;
        } else if (hasFollowups) {
          return <Chip label="در انتظار" color="warning" size="small" />;
        } else {
          return <Chip label="جدید" color="info" size="small" />;
        }
      },
    },
    {
      field: "services",
      headerName: "تعداد خدمات",
      align: "center",
      headerAlign: "center",
      sortable: false,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, any[]>) =>
        params.value?.length || 0,
    },
    {
      field: "createdOn",
      headerName: "تاریخ ایجاد",
      align: "center",
      headerAlign: "center",
      sortable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        params.value
          ? moment
              .from(params.value, "en", "YYYY-MM-DD")
              .format("jYYYY/jMM/jDD")
          : "-",
    },
  ];

  // بارگذاری اولیه داده‌ها
  useEffect(() => {
    if (userId && user?.token) {
      setLoading(true);
      getUserAgreements().finally(() => setLoading(false));
    }
  }, [
    userId,
    user?.token,
    paginationModel.page,
    paginationModel.pageSize,
    quickFilterValue,
  ]);

  // فوکوس روی فیلتر
  useEffect(() => {
    if (quickFilterRef.current) {
      quickFilterRef.current.focus();
    }
  }, [quickFilterValue]);

  if (!userId) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          کاربر انتخاب نشده است
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {(isPendingAgreements || isTransactionLoading || isDeleteLoading) && (
        <LinearProgress />
      )}

      <DataGrid
        columns={columns}
        rows={data}
        loading={loading}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pageSizeOptions={[10, 25, 50]}
        rowCount={rowCount}
        onPaginationModelChange={setPaginationModel}
        slots={{
          toolbar: () => (
            <GridToolbarContainer>
              <Stack
                direction="row"
                justifyContent="space-between"
                width="100%"
              >
                <GridToolbar />
                <GridToolbarQuickFilter
                  ref={quickFilterRef}
                  value={quickFilterValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="جستجو در قراردادها..."
                />
              </Stack>
            </GridToolbarContainer>
          ),
        }}
        slotProps={{
          toolbar: {
            printOptions: { getRowsToExport: true },
            csvOptions: { utf8WithBom: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5",
            borderBottom: "2px solid #e0e0e0",
          },
        }}
      />
    </Box>
  );
};

export default UserAgreements;
